import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Clock, User, Check } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import OptimizedImage from './OptimizedImage';

const humanizeSlug = (slug) => {
  if (!slug || typeof slug !== 'string') return '';
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const OfferCard = ({ offer, categoryCatalog = [] }) => {
  const { t, language } = useLanguage();

  const overlayCategoryLabel = () => {
    const slug = offer.primaryCategory || offer.slugCategory;
    if (slug) {
      const row = categoryCatalog.find((c) => c.slug === slug);
      if (row) {
        return (row.names && row.names[language]) || row.name || humanizeSlug(slug);
      }
      return humanizeSlug(slug);
    }
    const cats = offer.category || [];
    if (cats.some((c) => typeof c === 'string' && (c.includes('jet') || c.includes('ski')))) {
      return t('offerCard.jetSki');
    }
    if (cats.some((c) => typeof c === 'string' && c.includes('jetskis'))) {
      return t('offerCard.jetSki');
    }
    return t('offerCard.boatRental');
  };

  const getOfferUrl = () => {
    if (offer.slug) {
      if (offer.slugCategory) {
        return `/${offer.slugCategory}/${offer.slug}`;
      }
      return `/${offer.slug}`;
    }
    return `/offer/${offer.id}`;
  };

  const offerUrl = getOfferUrl();

  return (
    <Link
      to={offerUrl}
      className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:-translate-y-1 no-underline text-inherit"
    >
          {/* Image Container */}
          <div className="relative h-64 overflow-hidden">
            <OptimizedImage
              src={offer.image}
              alt={offer.title || offer.name}
              width={400}
              height={256}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            {offer.badge && (
              <div className="absolute top-4 left-4 bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
                {offer.badge}
              </div>
            )}
            <div className="absolute bottom-0 left-0 w-full h-2/3 bg-gradient-to-t from-black/60 to-transparent opacity-60 group-hover:opacity-80 transition-opacity"></div>
            
            <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end text-white">
              <div>
                <p className="text-sm font-medium opacity-90">{overlayCategoryLabel()}</p>
                {offer.isBundle ? (
                  <div className="space-y-0.5 mt-1">
                    <p className="text-base font-semibold leading-tight">
                      {t('offerCard.fromPrice')}{' '}
                      <span className="text-xl font-bold">{offer.priceFrom}€</span>
                      <span className="text-sm font-normal opacity-80"> TTC</span>
                    </p>
                    <p className="text-sm font-medium opacity-95">
                      {t('offerCard.fromDuration')}{' '}
                      <span className="font-bold">{offer.durationFromLabel}</span>
                    </p>
                  </div>
                ) : (
                  <p className="text-xl font-bold">
                    {offer.price}€ <span className="text-sm font-normal opacity-75">TTC</span>{' '}
                    <span className="text-sm font-normal">/ {offer.duration}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <h3 className="text-xl font-bold text-primary-950 mb-2 line-clamp-1 group-hover:text-accent-600 transition-colors">
              {offer.title || offer.name}
            </h3>
            
            <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>
                  {offer.isBundle
                    ? `${t('offerCard.fromDuration')} ${offer.durationFromLabel}${
                        offer.durationToLabel && offer.durationToLabel !== offer.durationFromLabel
                          ? ` (${offer.duration})`
                          : ''
                      }`
                    : offer.duration}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{offer.capacity}</span>
              </div>
            </div>

            <div className="space-y-2 mb-6">
              {(offer.features || offer.highlights || []).slice(0, 2).map((highlight, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <span className="line-clamp-1">{highlight}</span>
                </div>
              ))}
            </div>

            <Button 
              asChild
              className="w-full bg-primary-50 text-primary-900 hover:bg-primary-950 hover:text-white font-semibold transition-all"
              onClick={(e) => e.stopPropagation()}
            >
              <Link to={`${offerUrl}?booking=1`} onClick={(e) => e.stopPropagation()}>
                {t('offerCard.checkAvailability')}
              </Link>
            </Button>
          </div>
        </Link>
  );
};

export default OfferCard;
